/**
 * <product-info-configurator>
 *
 * A specialised PDP wrapper for configurator-style product templates.
 *
 * Behaviour deltas vs. the default <product-info>:
 *   - Page load renders with no preselected option, the cover-2 media as the
 *     active image, the in-button price label hidden and the ATC disabled.
 *   - Each time an option changes, the gallery jumps to the media whose
 *     alt text matches "OptionName: OptionValue" (handleized on both sides).
 *   - Only when every option has a value do we run the normal section refetch
 *     so the price label and ATC state catch up with the resolved variant.
 *
 * The matching map is built server-side in
 * sections/main-product-configurator.liquid and read from
 * <script data-configurator-image-map>.
 */
(function defineConfigurator() {
  if (customElements.get('product-info-configurator')) return;

  const upgrade = () => {
    const ProductInfo = customElements.get('product-info');
    if (!ProductInfo) {
      // product-info.js still loading; try again on next tick.
      requestAnimationFrame(upgrade);
      return;
    }

    class ProductInfoConfigurator extends ProductInfo {
      connectedCallback() {
        super.connectedCallback();
        this._configuratorReady = false;

        // Wait for child custom elements (variant-selects, product-form,
        // media-gallery) to be parsed before we touch them.
        Promise.all([
          customElements.whenDefined('variant-selects').catch(() => {}),
          customElements.whenDefined('product-form').catch(() => {}),
          customElements.whenDefined('media-gallery').catch(() => {}),
        ]).then(() => {
          this._initConfigurator();
        });
      }

      _initConfigurator() {
        this._imageMap = this._readImageMap();
        this._uncheckAll();
        this._setUnconfigured();
        // The cover-2 slide is rendered active by Liquid (forced_first_media_id),
        // so we don't need to slide on init. We do, however, need our own Swiper
        // instance because global.js binds the global `swiper` to the FIRST
        // .product_image_slider on the page, which is the regular main-product
        // gallery. Without this, our slideTo() calls would move the wrong slider.
        this._initSwiper();
        this._configuratorReady = true;
      }

      _initSwiper() {
        if (typeof Swiper === 'undefined') return;
        const sliderEl = this.querySelector('.product_image_slider');
        if (!sliderEl) return;
        if (sliderEl.swiper) {
          // The global swiper from global.js already attached itself to this
          // element. Reuse it rather than fighting with a duplicate instance.
          this._swiper = sliderEl.swiper;
          return;
        }
        try {
          this._swiper = new Swiper(sliderEl, {
            pagination: {
              el: sliderEl.querySelector('.swiper-pagination'),
              clickable: true,
            },
            navigation: {
              nextEl: sliderEl.querySelector('.swiper-button-next'),
              prevEl: sliderEl.querySelector('.swiper-button-prev'),
            },
          });
        } catch (e) {
          this._swiper = null;
        }
      }

      _readImageMap() {
        const el = this.querySelector('[data-configurator-image-map]');
        if (!el) return { covers: {}, byOption: {}, media: [] };
        let parsed;
        try {
          parsed = JSON.parse(el.textContent);
        } catch (e) {
          return { covers: {}, byOption: {}, media: [] };
        }
        const media = Array.isArray(parsed.media) ? parsed.media : [];
        // Build the byOption lookup client-side so we share one normalizer
        // with _normalizeKey and avoid the parity issues that come from
        // applying Shopify's handleize filter server-side and our regex pass
        // client-side. Each alt is expected in "Option Name: Option Value"
        // form; we split on the first colon and key by handleized name+value.
        const byOption = {};
        media.forEach((entry) => {
          if (!entry?.alt || !entry?.id) return;
          const idx = entry.alt.indexOf(':');
          if (idx === -1) return;
          const name = entry.alt.slice(0, idx);
          const value = entry.alt.slice(idx + 1);
          const key = this._normalizeKey(name, value);
          if (!byOption[key]) byOption[key] = entry.id;
        });
        return {
          covers: parsed.covers || {},
          byOption,
          media,
        };
      }

      _uncheckAll() {
        this.querySelectorAll('variant-selects input[type="radio"]').forEach((r) => {
          r.checked = false;
        });
        this.querySelectorAll('variant-selects select').forEach((s) => {
          s.selectedIndex = -1;
        });
      }

      _setUnconfigured() {
        this.classList.add('product-info-configurator--unconfigured');

        const idInput = this.querySelector('input[name="id"].product-variant-id');
        if (idInput) {
          idInput.value = '';
          idInput.setAttribute('disabled', 'disabled');
        }

        const submit = this.querySelector('[id^="ProductSubmitButton-"]');
        if (submit) submit.setAttribute('disabled', 'disabled');

        // The picker snippet renders `<span data-selected-value>` populated
        // with `option.selected_value` (Shopify's first-available variant on
        // initial page load). Clear them so the legend reads as "no
        // selection" until the user picks something. VariantSelects's change
        // handler will write the chosen value into the matching span on
        // click, and the post-AJAX DOM swap brings them back populated for
        // the resolved variant.
        this.querySelectorAll('variant-selects [data-selected-value]').forEach(
          (el) => {
            el.innerHTML = '';
          }
        );
      }

      _setConfigured() {
        this.classList.remove('product-info-configurator--unconfigured');
        // Re-enable the hidden variant id input. _setUnconfigured() disables it
        // on page load so the form can't be submitted without a resolved
        // variant; if we leave it disabled, the browser strips it from the
        // /cart/add.js payload and Shopify rejects the request with
        // "Required parameter missing or invalid: items".
        // The base class's updateVariantInputs() sets .value but never touches
        // the disabled attribute, so we have to clear it ourselves.
        this.querySelectorAll('input[name="id"].product-variant-id').forEach(
          (input) => input.removeAttribute('disabled')
        );
      }

      _normalizeKey(name, value) {
        // Match Shopify's `handleize` filter, which transliterates accented
        // characters (Soufflé → souffle) before the regex pass. We do that by
        // splitting accents into base + combining marks via NFD normalization
        // and then stripping the combining-mark range. Without this step,
        // "Midnight Soufflé / Periwinkle" normalizes to
        // "midnight-souffl-periwinkle" in JS but "midnight-souffle-periwinkle"
        // in Liquid, and the lookup misses.
        const h = (s) =>
          String(s || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
        return `${h(name)}__${h(value)}`;
      }

      _slideToMedia(mediaId) {
        const viewer = this.querySelector('[id^="GalleryViewer"]');
        if (!viewer) return;
        // Slide-level elements only. product-thumbnail.liquid also renders an
        // inner <deferred-media data-media-id> / <product-model data-media-id>
        // node inside each <li class="swiper-slide">, so a generic
        // querySelectorAll('[data-media-id]') doubles every slide and the
        // resulting findIndex doesn't line up with Swiper's slideTo, which
        // works against the swiper-slide siblings.
        const slides = Array.from(viewer.querySelectorAll('.swiper-slide'));
        const activeIndex = slides.findIndex(
          (el) => String(el.dataset.mediaId) === String(mediaId)
        );
        if (activeIndex === -1 || !this._swiper) return;
        this._swiper.slideTo(activeIndex);
      }

      _showImageForOption(name, value) {
        const id = this._imageMap?.byOption?.[this._normalizeKey(name, value)];
        if (!id) return;
        this._slideToMedia(id);
      }

      _allOptionsSelected() {
        const total = parseInt(this.dataset.optionCount || '0', 10);
        if (total <= 0) return false;

        const checkedRadios = this.querySelectorAll(
          'variant-selects input[type="radio"]:checked'
        ).length;
        const filledSelects = Array.from(
          this.querySelectorAll('variant-selects select')
        ).filter((s) => s.selectedIndex >= 0 && s.value).length;

        return checkedRadios + filledSelects >= total;
      }

      // Override: skip the gallery reorder + setActiveMedia jump that the base
      // class performs on every variant change. We keep the alt-matched image
      // we already swapped to in handleOptionValueChange.
      updateMedia() {
        // intentional no-op
      }

      // Override: the base class swaps in a fresh <variant-selects> from the
      // fetched section HTML. Our configurator picker intentionally renders
      // every option as unchecked (so we can show "no preselection" on page
      // load), which means the freshly inserted markup has nothing checked
      // and the user's clicks appear to vanish once the variant resolves.
      // We capture the user's current selections by their stable option-value
      // id, let the swap run, then re-apply them onto the new element. The
      // viewTransition utility is synchronous, so by the time super returns
      // the new <variant-selects> is already the first one in DOM order.
      updateOptionValues(html) {
        const previousSelections = this._captureSelectedValueIds();
        super.updateOptionValues(html);
        this._restoreSelectedValueIds(previousSelections);
      }

      _captureSelectedValueIds() {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return [];
        const checkedRadios = variantSelects.querySelectorAll(
          'input[type="radio"]:checked'
        );
        const selectedOptions = variantSelects.querySelectorAll(
          'select option[selected]'
        );
        return Array.from([...checkedRadios, ...selectedOptions])
          .map((el) => el.dataset?.optionValueId)
          .filter(Boolean);
      }

      _restoreSelectedValueIds(ids) {
        if (!ids?.length) return;
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;
        ids.forEach((id) => {
          const safeId = CSS.escape(String(id));
          const radio = variantSelects.querySelector(
            `input[type="radio"][data-option-value-id="${safeId}"]`
          );
          if (radio) {
            radio.checked = true;
            return;
          }
          const opt = variantSelects.querySelector(
            `option[data-option-value-id="${safeId}"]`
          );
          if (opt) {
            opt.setAttribute('selected', 'selected');
            const select = opt.closest('select');
            if (select) select.value = opt.value;
          }
        });
      }

      handleOptionValueChange(payload) {
        // The pub-sub fires for every <variant-selects> on the page, so make
        // sure we only react when the change actually came from inside this
        // configurator instance. Without this guard we'd react to clicks in
        // the regular main-product picker too (which happens to live on the
        // same page in the sleep-care template).
        const eventTarget = payload?.data?.event?.target;
        if (eventTarget && !this.contains(eventTarget)) return;

        if (!this._configuratorReady) {
          // Defer until our init has run; otherwise dataset.optionCount and
          // the image map may not be ready yet.
          requestAnimationFrame(() => this.handleOptionValueChange(payload));
          return;
        }

        const target = payload?.data?.target;
        const optionName =
          target?.dataset?.optionName ||
          target?.closest?.('[data-option-name]')?.dataset?.optionName;
        const optionValue =
          target?.dataset?.optionValue || target?.value || '';

        if (optionName && optionValue) {
          this._showImageForOption(optionName, optionValue);
        }

        if (this._allOptionsSelected()) {
          // Defer the post-fetch reveal until handleUpdateProductInfo finishes,
          // which uses requestAnimationFrame's microtask + fetch promise chain.
          // Listening for the framework's variantChange pub-sub would also work,
          // but we only need a class toggle here.
          super.handleOptionValueChange(payload);
          this._setConfigured();
        }
      }
    }

    customElements.define('product-info-configurator', ProductInfoConfigurator);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', upgrade);
  } else {
    upgrade();
  }
})();
